import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunitySpace } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { useCommunityDisplayName } from '../../_hooks/useCommunityDisplayName';
import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import { useSpacePurchasableTiers } from '../../_hooks/useSpacePurchasableTiers';
import { BaseUpgradeModal } from '../BaseUpgradeModal';
import { pollAccessRoute } from '../BaseUpgradeModal/useAccessPoll';
import { ChannelAccessRows } from './_components/ChannelAccessRows';
import type { ChannelUpgradeHandoff } from './useChannelUpgradeHandoff';

interface ChannelUpgradeModalProps {
  space: CommunitySpace;
  handoff: ChannelUpgradeHandoff;
}

/**
 * Sells access to one gated channel.
 *
 * Member-only by construction: the layout only opens this for a member with community
 * access facing a locked, known channel (see useChannelUpgradeGate), so there is no
 * guest/SSO path. Mounted only while open; the awaiting-access latch, the purchased
 * channel and the epilogue all live in useChannelUpgradeHandoff, in the layout.
 */
export const ChannelUpgradeModal: FC<ChannelUpgradeModalProps> = ({
  space,
  handoff: { awaiting, markAwaitingAccess, onAccessGranted },
}) => {
  const { t } = useTranslation();
  const communityName = useCommunityDisplayName();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const { canAccessSpace } = useSpaceAccess();
  const pricing = useSpacePurchasableTiers({ spaceId: space.id });
  const checkAccess = useMemo(
    () => pollAccessRoute(`/space_access?space_id=${encodeURIComponent(space.id)}`, 'space_accessible'),
    [space.id],
  );
  const purchase = awaiting?.space.id === space.id ? awaiting : null;
  const channels = purchase?.unlockedChannels ?? [];

  // Every locked channel the purchased tier opens, in sidebar order.
  const unlockedChannels = (tierId: string | undefined) => {
    const opened = (layoutData?.spaces ?? []).filter(
      (candidate) => tierId && candidate.premium_tier_ids.includes(tierId) && !canAccessSpace(candidate),
    );
    return opened.length > 0 ? opened : [space];
  };

  return (
    <BaseUpgradeModal
      isOpen
      title={t('community.upgrade_modal.channel_title', { channelName: space.name })}
      description={t('community.upgrade_modal.channel_description', { communityName })}
      accessStepsContent={{
        awaiting: {
          description: t('community.upgrade_modal.channel_processing_description', { count: channels.length }),
          body: (isTimedOut) => (
            <div role="status" className="flex flex-col gap-6">
              <ChannelAccessRows channels={channels} status={isTimedOut ? 'stalled' : 'pending'} />
              <p className="text-center text-sm leading-5 text-black/50">
                {t(
                  isTimedOut
                    ? 'community.upgrade_modal.payment_received'
                    : 'community.upgrade_modal.takes_a_few_seconds',
                )}
              </p>
            </div>
          ),
        },
        granted: {
          title: t('community.upgrade_modal.unlocked_title'),
          description: t('community.upgrade_modal.channel_unlocked_description', { count: channels.length }),
          body: <ChannelAccessRows channels={channels} status="unlocked" />,
          ctaLabel: t('community.upgrade_modal.go_to_channel', { channelName: space.name }),
        },
      }}
      impressionPageBlock={NectarPageBlock.CommunityChannelUpgradeModal}
      pricing={pricing}
      checkAccess={checkAccess}
      isAwaitingAccess={purchase !== null}
      onPaymentAccepted={(tierId) => markAwaitingAccess({ space, unlockedChannels: unlockedChannels(tierId) })}
      onAccessGranted={onAccessGranted}
      successRedirectTo={COMMUNITY_ROUTES.root}
    />
  );
};
